import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "../../lib/utils.ts"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "border border-[var(--figma-text)] bg-[var(--figma-text)] text-[var(--figma-bg)] hover:opacity-90 active:scale-[0.99]",
        destructive: "border border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-[var(--figma-border)] bg-transparent text-[var(--figma-text-muted)] hover:bg-[var(--figma-surface-2)] hover:text-[var(--figma-text)]",
        secondary: "border border-[var(--figma-border)] bg-[var(--figma-surface-2)] text-[var(--figma-text)] hover:opacity-90",
        ghost: "text-[var(--figma-text-muted)] hover:bg-[var(--figma-surface-2)] hover:text-[var(--figma-text)]",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-[46px] rounded-lg px-4 py-2",
        sm: "h-10 rounded-lg px-3 text-xs",
        lg: "h-12 rounded-lg px-8",
        icon: "h-10 w-10 rounded-lg",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
